import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EVENT_ARM_RECORDING, EVENT_ARM_RECORDING_ACK, EVENT_CLEAR_ARMED_RECORDING, EVENT_PAIR_EXTENSION_SESSION, EVENT_PAIR_EXTENSION_SESSION_ACK, EVENT_RECORDING_SNAPSHOT, EVENT_RECORDING_STATE, EVENT_REQUEST_RECORDING_SNAPSHOT, EVENT_STOP_RECORDING, EVENT_STOP_RECORDING_ACK, } from '@flow-doc/shared';
const ACK_TIMEOUT_MS = 4000;
function awaitAck(eventName, timeoutMs = ACK_TIMEOUT_MS) {
    return new Promise((resolve, reject) => {
        const handler = (event) => {
            const detail = event.detail;
            window.removeEventListener(eventName, handler);
            window.clearTimeout(timer);
            resolve(detail);
        };
        const timer = window.setTimeout(() => {
            window.removeEventListener(eventName, handler);
            reject(new Error(`Timed out waiting for ${eventName}`));
        }, timeoutMs);
        window.addEventListener(eventName, handler, { once: true });
    });
}
/**
 * Talks to the Cricket Studio browser extension via window CustomEvents
 * (forwarded by the content script). Returns the live recording snapshot,
 * plus imperative helpers to arm, stop, or refresh.
 *
 * Consumers should gate the UI on `supported` (mirrors extension-installed
 * detection) and treat `snapshot === null` as "no signal yet".
 */
export function useExtensionRecordingBridge(options = { enabled: true }) {
    const { enabled } = options;
    const [snapshot, setSnapshot] = useState(null);
    const [supported, setSupported] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    const requestSnapshot = useCallback(() => {
        if (!enabled)
            return;
        window.dispatchEvent(new CustomEvent(EVENT_REQUEST_RECORDING_SNAPSHOT));
    }, [enabled]);
    useEffect(() => {
        if (!enabled) {
            setSnapshot(null);
            setSupported(false);
            return;
        }
        const handleState = (event) => {
            if (!mountedRef.current)
                return;
            const detail = event.detail;
            if (!detail)
                return;
            setSnapshot(detail);
            setSupported(true);
            console.info('[RecordingBridge] state', detail);
        };
        const handleSnapshot = (event) => {
            if (!mountedRef.current)
                return;
            const detail = event.detail;
            if (!detail)
                return;
            setSnapshot(detail);
            setSupported(true);
            console.info('[RecordingBridge] snapshot', detail);
        };
        window.addEventListener(EVENT_RECORDING_STATE, handleState);
        window.addEventListener(EVENT_RECORDING_SNAPSHOT, handleSnapshot);
        // Nudge the extension to send us the current snapshot.
        requestSnapshot();
        const retry = window.setTimeout(requestSnapshot, 400);
        const retry2 = window.setTimeout(requestSnapshot, 1500);
        return () => {
            window.removeEventListener(EVENT_RECORDING_STATE, handleState);
            window.removeEventListener(EVENT_RECORDING_SNAPSHOT, handleSnapshot);
            window.clearTimeout(retry);
            window.clearTimeout(retry2);
        };
    }, [enabled, requestSnapshot]);
    const arm = useCallback(async (prefs) => {
        const ack = awaitAck(EVENT_ARM_RECORDING_ACK);
        window.dispatchEvent(new CustomEvent(EVENT_ARM_RECORDING, { detail: prefs }));
        try {
            const result = await ack;
            if (result?.ok) {
                setSupported(true);
            }
            return result;
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to arm recording';
            return { ok: false, error: message };
        }
    }, []);
    const pair = useCallback(async (payload) => {
        const ack = awaitAck(EVENT_PAIR_EXTENSION_SESSION_ACK);
        window.dispatchEvent(new CustomEvent(EVENT_PAIR_EXTENSION_SESSION, { detail: payload }));
        try {
            const result = await ack;
            if (result?.ok) {
                setSupported(true);
            }
            return result;
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to pair extension session';
            return { ok: false, error: message };
        }
    }, []);
    const stop = useCallback(async (outcome) => {
        const ack = awaitAck(EVENT_STOP_RECORDING_ACK, 60000);
        window.dispatchEvent(new CustomEvent(EVENT_STOP_RECORDING, {
            detail: outcome ? { flowOutcome: outcome } : undefined,
        }));
        try {
            return await ack;
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to stop recording';
            return { ok: false, error: message };
        }
    }, []);
    const clearArmed = useCallback(() => {
        window.dispatchEvent(new CustomEvent(EVENT_CLEAR_ARMED_RECORDING));
    }, []);
    return useMemo(() => ({
        snapshot,
        supported,
        arm,
        pair,
        stop,
        clearArmed,
        refresh: requestSnapshot,
    }), [snapshot, supported, arm, pair, stop, clearArmed, requestSnapshot]);
}
